import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, X, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { http, apiError } from "@/lib/api";
import { useOrg } from "@/context/OrgContext";
import { useAuth } from "@/context/AuthContext";
import { Card, SectionTitle, SeverityChip, Spinner } from "@/components/Kit";

export default function Applications() {
  const { orgId } = useOrg();
  const { can } = useAuth();
  const canManage = can("appsec.applications", "manage");
  const navigate = useNavigate();
  const [apps, setApps] = useState([]);
  const [show, setShow] = useState(false);
  const [edit, setEdit] = useState(null);

  const load = useCallback(() => http.get("/applications", { params: { org_id: orgId } }).then((r) => setApps(r.data)), [orgId]);
  useEffect(() => { setApps([]); }, [orgId]);
  useEffect(() => { if (orgId) load(); }, [load, orgId]);

  const remove = async (a, e) => {
    e.stopPropagation();
    if (!window.confirm(`Delete ${a.name}? This removes the application and its findings.`)) return;
    try { await http.delete(`/applications/${a.id}`, { params: { org_id: orgId } }); toast.success("Application deleted"); load(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  return (
    <div className="p-6 max-w-[1400px] mx-auto fade-up">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="font-display text-3xl font-bold text-[#A5300F]">Applications</h1>
          <p className="text-sm text-[#8A8175] mt-1">{apps.length} applications in this organisation.</p>
        </div>
        {canManage && (
          <button onClick={() => setShow(true)} data-testid="new-app-btn"
            className="flex items-center gap-2 bg-[#A5300F] text-white font-semibold text-sm px-4 py-2 rounded-md hover:bg-[#8F2A0D]">
            <Plus className="w-4 h-4" /> New Application
          </button>
        )}
      </div>

      <Card>
        <table className="w-full text-sm" data-testid="apps-table">
          <thead><tr className="text-[11px] uppercase tracking-wider text-[#8A8175] text-left bg-[#FFF5EB] border-b border-[#ECE6DD]">
            <th className="py-2.5 px-4">Application</th><th>Owner</th><th>Criticality</th><th className="text-center">Internet</th>
            <th>Last Scan</th><th className="text-center">Open Findings (C/H/M/L)</th>{canManage && <th className="text-right pr-4">Actions</th>}
          </tr></thead>
          <tbody>
            {apps.map((a) => (
              <tr key={a.id} onClick={() => navigate(`/applications/${a.id}`)}
                className="border-b border-[#F4EFE8] hover:bg-[#FBF7F1] cursor-pointer" data-testid={`app-row-${a.id}`}>
                <td className="py-3 px-4 font-semibold">{a.name}</td>
                <td className="text-[#5C554C]">{a.business_owner || "—"}</td>
                <td>{a.criticality}</td>
                <td className="text-center">{a.internet_facing ? "Yes" : "No"}</td>
                <td className="text-[#8A8175] text-xs">{a.last_scan_at ? new Date(a.last_scan_at).toLocaleDateString("en-GB") : "Never"}</td>
                <td className="text-center font-semibold">
                  <span style={{ color: "#A5300F" }}>{a.open_findings.Critical}</span> /
                  <span style={{ color: "#D55816" }}> {a.open_findings.High}</span> /
                  <span style={{ color: "#E39629" }}> {a.open_findings.Medium}</span> /
                  <span style={{ color: "#7F5F52" }}> {a.open_findings.Low}</span>
                </td>
                {canManage && (
                  <td className="text-right pr-4 whitespace-nowrap">
                    <button onClick={(e) => { e.stopPropagation(); setEdit(a); }} data-testid={`edit-app-${a.id}`} className="p-1.5 text-[#8A8175] hover:text-[#A5300F]"><Pencil className="w-4 h-4" /></button>
                    <button onClick={(e) => remove(a, e)} data-testid={`delete-app-${a.id}`} className="p-1.5 text-[#8A8175] hover:text-[#A5300F]"><Trash2 className="w-4 h-4" /></button>
                  </td>
                )}
              </tr>
            ))}
            {apps.length === 0 && <tr><td colSpan={canManage ? 7 : 6} className="py-8 text-center text-[#B5AC9E]">No applications yet.</td></tr>}
          </tbody>
        </table>
      </Card>

      {show && <NewAppModal orgId={orgId} onClose={() => setShow(false)} onSaved={() => { setShow(false); load(); }} />}
      {edit && <EditAppModal orgId={orgId} app={edit} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); }} />}
    </div>
  );
}

function EditAppModal({ orgId, app, onClose, onSaved }) {
  const [f, setF] = useState({ name: app.name || "", business_owner: app.business_owner || "",
    criticality: app.criticality || "Medium", internet_facing: !!app.internet_facing,
    data_classification: app.data_classification || "Internal", repo_url: app.repo_url || "" });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const save = async () => {
    setBusy(true);
    try { await http.patch(`/applications/${app.id}`, f, { params: { org_id: orgId } }); toast.success("Application updated"); onSaved(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/40 flex items-start justify-center z-50 p-4 overflow-y-auto" onClick={onClose}>
      <Card className="w-full max-w-lg my-8" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b border-[#ECE6DD]">
          <SectionTitle>Edit Application</SectionTitle>
          <button onClick={onClose}><X className="w-4 h-4 text-[#8A8175]" /></button>
        </div>
        <div className="p-5 space-y-3">
          <Field label="Name"><input data-testid="edit-app-name" value={f.name} onChange={(e) => set("name", e.target.value)} className={inp} /></Field>
          <Field label="Business Owner"><input value={f.business_owner} onChange={(e) => set("business_owner", e.target.value)} className={inp} /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Criticality">
              <select value={f.criticality} onChange={(e) => set("criticality", e.target.value)} className={inp}>
                {["Critical", "High", "Medium", "Low"].map((c) => <option key={c}>{c}</option>)}
              </select>
            </Field>
            <Field label="Data Classification">
              <select value={f.data_classification} onChange={(e) => set("data_classification", e.target.value)} className={inp}>
                {["Public", "Internal", "Confidential", "PCI", "PII"].map((c) => <option key={c}>{c}</option>)}
              </select>
            </Field>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={f.internet_facing} onChange={(e) => set("internet_facing", e.target.checked)} className="accent-[#A5300F]" />
            Internet-facing
          </label>
          <Field label="Repository URL (display only)"><input value={f.repo_url} onChange={(e) => set("repo_url", e.target.value)} className={inp} /></Field>
        </div>
        <div className="flex justify-end gap-2 px-5 py-3 border-t border-[#ECE6DD]">
          <button onClick={onClose} className="px-3 py-2 text-sm border border-[#E7D9C6] rounded-md">Cancel</button>
          <button onClick={save} disabled={busy || !f.name} data-testid="save-app-edit-btn"
            className="px-4 py-2 text-sm bg-[#A5300F] text-white font-semibold rounded-md hover:bg-[#8F2A0D] disabled:opacity-50 flex items-center gap-2">
            {busy && <Spinner />} Save
          </button>
        </div>
      </Card>
    </div>
  );
}

function NewAppModal({ orgId, onClose, onSaved }) {
  const [f, setF] = useState({ name: "", business_owner: "", criticality: "Medium",
    internet_facing: false, data_classification: "Internal", repo_url: "" });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const save = async () => {
    setBusy(true);
    try {
      await http.post("/applications", { org_id: orgId, ...f });
      toast.success("Application created"); onSaved();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-start justify-center z-50 p-4 overflow-y-auto" onClick={onClose}>
      <Card className="w-full max-w-lg my-8" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b border-[#ECE6DD]">
          <SectionTitle>New Application</SectionTitle>
          <button onClick={onClose}><X className="w-4 h-4 text-[#8A8175]" /></button>
        </div>
        <div className="p-5 space-y-3">
          <Field label="Name"><input data-testid="app-name" value={f.name} onChange={(e) => set("name", e.target.value)} className={inp} /></Field>
          <Field label="Business Owner"><input data-testid="app-owner" value={f.business_owner} onChange={(e) => set("business_owner", e.target.value)} className={inp} /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Criticality">
              <select data-testid="app-criticality" value={f.criticality} onChange={(e) => set("criticality", e.target.value)} className={inp}>
                {["Critical", "High", "Medium", "Low"].map((c) => <option key={c}>{c}</option>)}
              </select>
            </Field>
            <Field label="Data Classification">
              <select value={f.data_classification} onChange={(e) => set("data_classification", e.target.value)} className={inp}>
                {["Public", "Internal", "Confidential", "PCI", "PII"].map((c) => <option key={c}>{c}</option>)}
              </select>
            </Field>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" data-testid="app-internet" checked={f.internet_facing} onChange={(e) => set("internet_facing", e.target.checked)} className="accent-[#A5300F]" />
            Internet-facing
          </label>
          <Field label="Repository URL (display only)"><input value={f.repo_url} onChange={(e) => set("repo_url", e.target.value)} className={inp} /></Field>
        </div>
        <div className="flex justify-end gap-2 px-5 py-3 border-t border-[#ECE6DD]">
          <button onClick={onClose} className="px-3 py-2 text-sm border border-[#E7D9C6] rounded-md">Cancel</button>
          <button onClick={save} disabled={busy || !f.name} data-testid="save-app-btn"
            className="px-4 py-2 text-sm bg-[#A5300F] text-white font-semibold rounded-md hover:bg-[#8F2A0D] disabled:opacity-50 flex items-center gap-2">
            {busy && <Spinner />} Create
          </button>
        </div>
      </Card>
    </div>
  );
}

const inp = "w-full mt-1 bg-white border border-[#E7D9C6] rounded-md px-3 py-2 text-sm focus:outline-none focus:border-[#A5300F]";
const Field = ({ label, children }) => (
  <div><label className="text-[11px] uppercase tracking-wider text-[#8A8175] font-semibold">{label}</label>{children}</div>
);
